// app-recreation-project/src/components/GlobalRuntimeErrorOverlay.jsx
import React, { useEffect, useState } from 'react';

/**
 * NOTE:
 * If you are seeing a white screen in production, that usually means a runtime error
 * is happening before React can show its own error boundary. This overlay lets you
 * SEE that error on-screen instead of guessing.
 */

/**
 * GlobalRuntimeErrorOverlay
 * -------------------------
 * This component listens for global runtime errors and unhandled promise rejections.
 * If something blows up outside React (or before the ErrorBoundary can render),
 * it will show a small overlay instead of leaving the user with a blank white screen.
 */
const GlobalRuntimeErrorOverlay = () => {
  const [errorInfo, setErrorInfo] = useState(null);

  useEffect(() => {
    const handleError = (event) => {
      // Prevent the browser's default error popup
      event.preventDefault?.();

      setErrorInfo({
        type: 'error',
        message: event?.error?.message || event?.message || 'Unknown runtime error',
        stack: event?.error?.stack || null,
      });

      // Also log to console for debugging
      // eslint-disable-next-line no-console
      console.error('Global window.onerror captured:', event);
    };

    const handleUnhandledRejection = (event) => {
      event.preventDefault?.();
      const reason = event?.reason;

      setErrorInfo({
        type: 'unhandledrejection',
        message:
          (reason && reason.message) ||
          (typeof reason === 'string' ? reason : 'Unhandled promise rejection'),
        stack: reason && reason.stack ? reason.stack : null,
      });

      // eslint-disable-next-line no-console
      console.error('Global unhandledrejection captured:', event);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  if (!errorInfo) return null;

  return (
    <div className="fixed bottom-4 right-4 max-w-md z-[9999]">
      <div className="bg-red-50 border border-red-300 text-red-900 rounded-lg shadow-lg p-4 text-sm">
        <div className="flex items-center justify-between mb-2">
          <span className="font-semibold">
            Runtime Error ({errorInfo.type})
          </span>
          <button
            type="button"
            onClick={() => setErrorInfo(null)}
            className="ml-3 text-xs px-2 py-1 rounded bg-red-100 hover:bg-red-200"
          >
            Dismiss
          </button>
        </div>
        <p className="mb-2 break-words">
          {errorInfo.message}
        </p>
        {errorInfo.stack && (
          <pre className="max-h-40 overflow-auto text-[10px] leading-snug bg-white border border-red-100 rounded p-2">
            {errorInfo.stack}
          </pre>
        )}
        <p className="mt-2 text-[11px] text-red-700">
          This overlay is for debugging. If you see this in production, take a screenshot
          and share the error message and stack trace.
        </p>
      </div>
    </div>
  );
};

export default GlobalRuntimeErrorOverlay;